import { CLICK_ON_PAID_FEATURE, UPGRADE } from 'constants/mixpanel';
import { loadFonts } from 'hooks/useFont';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { store, useAppDispatch, useAppSelector } from 'modules/Editor/store';
import { selectBrandIdx } from 'modules/Editor/store/editorSettingsSelector';
import { setIsGatedFontUploadModalOpen } from 'modules/Editor/store/editorSettingsSlice';
import { useBrand, useUser } from 'modules/User';
import { BrandOptionDropdown } from 'modules/User/components/BrandOptionDropdown';
import { DocumentFontProvider } from 'modules/common/components/FontFamilySelect/DocumentFonts';
import {
  BRAND_DROPDOWN_CONTAINER_CLASSNAME,
  BRAND_DROPDOWN_TRIGGER_CLASSNAME,
} from 'modules/common/components/FontFamilySelect/SelectList.config';
import { ReactNode, useCallback, useRef, useState } from 'react';
import { CustomFontState, CustomFontUploadProps } from 'types/customFont.types';

import { getAllFontsInfograph, getBrandFonts, redirectToBrandPage } from './FontProvider.helpers';
import { useLoadBrandFonts } from './useLoadBrandFonts';

const MAX_DOCUMENT_FONTS = 5;

/**
 * Provides the font select component with:
 * 1. Document fonts (from the infograph)
 * 2. The fonts from the selected brand kit
 */
export const FontProvider = ({ children }: { children: ReactNode }) => {
  const dispatch = useAppDispatch();
  const brandIdx = useAppSelector(selectBrandIdx);
  const [documentFonts, setDocumentFonts] = useState<string[]>([]);

  const { isEducationUser, isBusinessUser, isEnterpriseUser } = useUser();

  // Only business and enterprise users can upload fonts
  const hasFontUploadPermission = isBusinessUser || isEnterpriseUser;

  // Education user should not have any brand features
  const showBrandSection = !isEducationUser;

  const { data: brandData } = useBrand();

  const selectedBrand = brandData?.[brandIdx];
  const selectedBrandFontsRef = useRef<string[]>([]);
  const selectedBrandFonts: string[] = getBrandFonts(selectedBrand);

  // sync the brand fonts with the ref - to avoid adding it to the dependency array
  selectedBrandFontsRef.current = selectedBrandFonts;

  // Custom uploaded fonts dictionary <fontName, fontData> for font loading & displaying font image in the font select
  const customFontsMapRef = useRef(new Map<string, CustomFontState>());

  const onSetDocumentFonts = useCallback((brandFonts = selectedBrandFontsRef.current) => {
    const infograph = store.getState().infograph;
    const fonts = getAllFontsInfograph(infograph);

    // filter out the brand fonts (brand fonts might or might not have been loaded yet)
    const fontsWithoutBrand = fonts.filter((font) => !brandFonts.some((brandFont) => brandFont === font));

    setDocumentFonts(fontsWithoutBrand.slice(0, MAX_DOCUMENT_FONTS));
  }, []);

  // loads the brand fonts when the brand is updated / loaded
  // & populates the customFontsMapRef with the custom fonts
  useLoadBrandFonts({ onBrandLoaded: onSetDocumentFonts, customFontsMapRef, loadFonts, selectedBrandFonts });

  const setGatedFontModalVisibility = useCallback(
    (isOpen: boolean) => dispatch(setIsGatedFontUploadModalOpen(isOpen)),
    [dispatch],
  );

  const fontUploadOnClick = (hasUploadPermission?: boolean) => {
    if (hasUploadPermission) return redirectToBrandPage();

    setGatedFontModalVisibility(true);
    Mixpanel.track(CLICK_ON_PAID_FEATURE.eventName, {
      Feature: UPGRADE.feature.CustomFont,
    });
  };

  const fontUploadProps: CustomFontUploadProps = {
    hasUploadPermission: hasFontUploadPermission,
    onClick: () => fontUploadOnClick(hasFontUploadPermission),
  };

  // loads the brand fonts when the brand is updated / loaded
  // & populates the customFontsMapRef with the custom fonts
  useLoadBrandFonts({ onBrandLoaded: onSetDocumentFonts, customFontsMapRef, loadFonts, selectedBrandFonts });

  return (
    <DocumentFontProvider
      showBrandSection={showBrandSection}
      brandFonts={selectedBrandFonts}
      brandOptions={
        <BrandOptionDropdown
          contentClassName={BRAND_DROPDOWN_CONTAINER_CLASSNAME}
          triggerClassName={BRAND_DROPDOWN_TRIGGER_CLASSNAME}
          triggerProps={{ width: 32, height: 7 }}
        />
      }
      customFontsMap={customFontsMapRef.current}
      documentFonts={documentFonts}
      fontUploadProps={fontUploadProps}
      onOpen={onSetDocumentFonts}
    >
      {children}
    </DocumentFontProvider>
  );
};
